import type { IRTCEngine } from '@volcengine/rtc';
import { installVolcRtcPeerConnectionProbe } from './peer-connection-probe';
import { getVolcRtcSdk } from './sdk';

export const VOLC_VIDEO_ENCODER_CONFIG = {
  width: { max: 960 },
  height: { max: 540 },
  frameRate: { max: 24 },
  maxKbps: 1200,
  contentHint: 'motion' as const,
};

/** 弱网降级档，也是手机上行上限：分辨率和码率一起降，避免 540p 被硬压码率后发虚。 */
export const VOLC_DEGRADED_VIDEO_ENCODER_CONFIG = {
  width: { max: 480 },
  height: { max: 270 },
  frameRate: { max: 24 },
  maxKbps: 600,
  contentHint: 'motion' as const,
};

/** 手机（非平板）用 270p 档。iPad / 桌面仍走 540p，由会话层按 UA 选择。 */
export const isMobilePhoneUserAgent = (userAgent: string): boolean =>
  /iPhone|iPod|Android.+Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

/**
 * 对齐火山 `RTCAutoPlayPolicy.AUTO_PLAY`。
 * 订阅后由 SDK 自动播远端音视频；微信里仍可能被 X5 拦住，再在 JSBridge 里补一次 engine.play。
 */
export const VOLC_AUTO_PLAY_POLICY_AUTO = 0;

interface VolcRtcSdkModule {
  default: {
    setParameter: (key: 'ENABLE_PLAY_AFTER_CLICK', value: boolean) => unknown;
    createEngine: (appId: string, config?: { autoPlayPolicy?: number }) => IRTCEngine;
    destroyEngine: (engine: IRTCEngine) => void;
  };
  StreamIndex: { STREAM_INDEX_MAIN: number };
  VideoSourceType: { VIDEO_SOURCE_TYPE_EXTERNAL: number };
}

const loadVolcRtcSdk = async (): Promise<VolcRtcSdkModule> => getVolcRtcSdk() as unknown as VolcRtcSdkModule;

let engine: IRTCEngine | null = null;
let engineAppId: string | null = null;
let enginePromise: Promise<IRTCEngine> | null = null;
let destroyEngineFn: ((current: IRTCEngine) => void) | null = null;
let activeSessionCount = 0;
let engineGeneration = 0;

const destroyCurrentEngine = (): void => {
  engineGeneration += 1;
  const current = engine;
  const destroyEngine = destroyEngineFn;
  engine = null;
  engineAppId = null;
  enginePromise = null;
  destroyEngineFn = null;
  if (current && destroyEngine) {
    destroyEngine(current);
  }
};

export const retainVolcRtcEngine = (): (() => void) => {
  activeSessionCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    activeSessionCount = Math.max(0, activeSessionCount - 1);
    if (activeSessionCount === 0 && (engine || enginePromise)) {
      destroyCurrentEngine();
    }
  };
};

/**
 * claim 成功后用 claim.rtc.app_id 创建。挂断、关直播、离开页都 destroy。
 */
export const ensureVolcRtcEngine = async (
  appId: string,
  loadSdk: () => Promise<VolcRtcSdkModule> = loadVolcRtcSdk,
): Promise<IRTCEngine> => {
  const normalizedAppId = appId.trim();
  if (!normalizedAppId) {
    throw new Error('volc rtc createEngine requires appId');
  }

  if (engine && engineAppId === normalizedAppId) {
    return engine;
  }
  if (enginePromise && engineAppId === normalizedAppId) {
    return enginePromise;
  }
  if (engine || enginePromise) {
    destroyCurrentEngine();
  }

  if (process.env.NODE_ENV === 'development') {
    installVolcRtcPeerConnectionProbe();
  }

  const generation = engineGeneration;
  engineAppId = normalizedAppId;
  const pendingEngine = (async () => {
    const sdk = await loadSdk();
    sdk.default.setParameter('ENABLE_PLAY_AFTER_CLICK', true);
    const nextEngine = sdk.default.createEngine(normalizedAppId, {
      autoPlayPolicy: VOLC_AUTO_PLAY_POLICY_AUTO,
    });
    try {
      await nextEngine.setVideoEncoderConfig(VOLC_VIDEO_ENCODER_CONFIG);
      await nextEngine.setVideoSourceType(
        sdk.StreamIndex.STREAM_INDEX_MAIN,
        sdk.VideoSourceType.VIDEO_SOURCE_TYPE_EXTERNAL,
      );
      if (generation !== engineGeneration) {
        throw new Error('volc rtc engine initialization cancelled');
      }
      destroyEngineFn = sdk.default.destroyEngine;
      engine = nextEngine;
      return nextEngine;
    } catch (error) {
      sdk.default.destroyEngine(nextEngine);
      if (engineAppId === normalizedAppId) {
        engineAppId = null;
      }
      throw error;
    }
  })();
  enginePromise = pendingEngine;

  try {
    return await pendingEngine;
  } catch (error) {
    if (enginePromise === pendingEngine) {
      enginePromise = null;
      destroyEngineFn = null;
      if (engineAppId === normalizedAppId) {
        engineAppId = null;
      }
    }
    throw error;
  }
};

export const getVolcRtcEngine = (): IRTCEngine | null => engine;

export const destroyVolcRtcEngine = (): void => {
  destroyCurrentEngine();
};
